// @ts-strict-ignore
import * as React from 'react';
import Layout from 'antd/lib/layout';
import { Content, Footer } from 'antd/lib/layout/layout';
import styles from './styles/stepStyles';
import Text from 'antd/lib/typography/Text';
import { ReleaseEventsTimelineSection } from '@/components/releaseOverview/ReleaseTimelineSection';
import { ReleaseTimelineSectionContext } from '@/components/timelineEvents/EventsTimeline';
import { ReleaseStepInactiveContext } from '@/contexts';
import { RootState } from '@/reducers/reducers';
import { FeatureSection, Nullable, ReleaseStepComponentProps } from '@/typescriptHelpers';
import {
    selectApp,
    selectAutomationById,
    selectIsRunwayOrg,
    selectRelease,
    selectReleaseStep,
    selectStepRun,
    selectTranslationsIntegration,
    selectVCSIntegration,
    selectWorkflowEnabledSteps,
} from '@/selectors/selectors';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import {
    AppPlatform,
    AppStoreType,
    AutomationType,
    ReleaseStepTranslationsData,
    ReleaseStepType,
    ReleaseTimelinePhase,
    RunwayResponseType,
    TranslationsData,
} from '@/types';
import { TranslationsStepRunSummaryData } from '@/types/translations';
import { TranslationStrings } from '@/components/translations/TranslationStrings';
import Space from 'antd/lib/space';
import { CheckOutlined, CloseOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { runwayColors } from '@/components/styles/globalStyles';
import Select from 'antd/lib/select';
import PercentTranslationsCompleteComponent from '@/components/translations/PercentTranslationsCompleteComponent';
import PermissionedActionButton from '@/components/HOCs/PermissionedActionButton';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { ReleaseStepUpdatable } from '@/actions/updatable';
import { UpdateTranslationsApprovalRequest } from '@/actions/release';
import { EmptyStateWrapper } from './releaseEmptyState/EmptyStateWrapper';
import IntegrationIcon from '@/components/icons/IntegrationIcon';
import { globalMessage } from '@/utils/globalMessage';
import {
    displayTextForAppPlatform,
    exportTranslationsExplainerMessage,
    uploadTranslationsExplainerMessage,
} from '@/helpers/displayHelpers';
import { tryUpdateReleaseStep } from '@/actions/releaseStep';
import { useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { StepRunUpdatable } from '@/actions/stepRun';
import { getAutomationIdByType } from '@/helpers';
import { ReleaseStepOrStepRunComponentProps } from '@/components/ValidatedRouteComponents';
import TooltipInfoIcon from './helperComponents/TooltipInfoIcon';

interface State {
    workingBranchEnabled?: boolean;
    // Keyed by platform rather than position, so a reordered or different step run doesn't move the selection.
    selectedPlatformKey?: string;
}

interface TranslationsPlatformData {
    platform: AppPlatform;
    appStoreType: AppStoreType;
    translationsData: Nullable<TranslationsData>;
}

const translationsPlatformKey = (platformData: TranslationsPlatformData) =>
    `${platformData.platform}-${platformData.appStoreType}`;

class StepContentTranslations extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            workingBranchEnabled: null,
        };
    }

    static getDerivedStateFromProps(props: Props, state: State) {
        if (props.showBranchSelectionField && !props.releaseBranchExists && state.workingBranchEnabled === null) {
            // default to showing items from working branch when release branch doesn't exist
            return {
                ...state,
                workingBranchEnabled: true,
            };
        }

        return null;
    }

    render() {
        const selectedPlatform =
            this.props.translationsDataByPlatform.find(
                (platformData) => translationsPlatformKey(platformData) === this.state.selectedPlatformKey,
            ) ?? this.props.translationsDataByPlatform[0];
        const translationsData = selectedPlatform?.translationsData;
        const keys = translationsData?.keys ?? [];
        const workingBranchKeys = translationsData?.workingBranchKeys ?? [];

        return (
            <Layout>
                <Content
                    style={{
                        ...styles.innerContent,
                        paddingTop: 0,
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            paddingTop: 40,
                            paddingBottom: 36,
                        }}
                    >
                        <div style={{ marginRight: 36 }}>
                            <PercentTranslationsCompleteComponent
                                workingBranchEnabled={this.state.workingBranchEnabled}
                                releaseId={this.props.releaseId}
                                workingBranchKeys={workingBranchKeys}
                                keys={keys}
                            />
                        </div>
                        <Space direction="vertical">
                            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                                {this.props.translationsDataByPlatform.length > 1 && (
                                    <Space direction="vertical" style={{ marginRight: 24 }}>
                                        <Text strong>Platform</Text>
                                        <Select
                                            value={selectedPlatform && translationsPlatformKey(selectedPlatform)}
                                            onChange={(selectedPlatformKey) =>
                                                this.setState({ ...this.state, selectedPlatformKey })
                                            }
                                            style={{ minWidth: 200 }}
                                        >
                                            {this.props.translationsDataByPlatform.map((platformData) => (
                                                <Select.Option
                                                    key={translationsPlatformKey(platformData)}
                                                    value={translationsPlatformKey(platformData)}
                                                >
                                                    {displayTextForAppPlatform(
                                                        platformData.platform,
                                                        platformData.appStoreType,
                                                    )}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Space>
                                )}
                                {this.props.showBranchSelectionField && (
                                    <Space direction="vertical" style={{ marginRight: 24 }}>
                                        <Space>
                                            {this.props.vcsIntegrationId && (
                                                <IntegrationIcon
                                                    integrationId={this.props.vcsIntegrationId}
                                                    size={20}
                                                />
                                            )}
                                            <Text strong>Base branch</Text>
                                            <TooltipInfoIcon tooltipContent="View progress of translations against the selected branch" />
                                        </Space>
                                        <Space>
                                            <Select
                                                onChange={(value) => {
                                                    const workingBranchEnabled = value === this.props.workingBranch;

                                                    this.setState({
                                                        ...this.state,
                                                        workingBranchEnabled,
                                                    });
                                                }}
                                                defaultValue={
                                                    this.props.releaseBranchExists ?
                                                        this.props.releaseBranch
                                                    :   this.props.workingBranch
                                                }
                                                style={{ minWidth: 300 }}
                                            >
                                                <Select.Option
                                                    value={this.props.releaseBranch}
                                                    disabled={!this.props.releaseBranchExists}
                                                >
                                                    {this.props.releaseBranch}
                                                    {!this.props.releaseBranchExists && ' (not created)'}
                                                </Select.Option>
                                                <Select.Option value={this.props.workingBranch}>
                                                    {this.props.workingBranch}
                                                </Select.Option>
                                            </Select>
                                        </Space>
                                    </Space>
                                )}
                            </div>
                            {this.state.workingBranchEnabled && (
                                <Text style={{ color: runwayColors.statusYellow }}>
                                    {this.props.releaseBranchExists ?
                                        'Release branch not selected'
                                    :   'Release branch not created'}
                                </Text>
                            )}
                        </Space>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'flex-end',
                                paddingLeft: 24,
                                flexGrow: 1,
                            }}
                        ></div>
                        <div style={{ marginTop: '-60px' }}>
                            <ReleaseEventsTimelineSection
                                orgId={this.props.orgId}
                                appId={this.props.appId}
                                releaseId={this.props.releaseId}
                                stepRunId={this.props.stepRunComponentProps?.stepRunId}
                                context={ReleaseTimelineSectionContext.ReleaseStep}
                                releaseStepType={this.props.releaseStepType}
                            />
                        </div>
                    </div>
                    <TranslationStrings
                        orgId={this.props.orgId}
                        appId={this.props.appId}
                        stepRunId={this.props.stepRunId}
                        releaseId={this.props.releaseId}
                        workingBranchEnabled={
                            this.state.workingBranchEnabled === null ? false : this.state.workingBranchEnabled
                        }
                        mergeRunwayPRsEnabled={this.props.mergeRunwayPRsEnabled}
                        releaseBranchName={this.props.releaseBranch}
                        releaseStepType={this.props.releaseStepType}
                        sourceLocale={translationsData?.sourceLocale ?? ''}
                        latestPullRequest={translationsData?.latestPullRequest}
                        providerState={translationsData?.providerState}
                        keys={keys}
                        workingBranchKeys={workingBranchKeys}
                        autoSyncTranslationsAutomationId={this.props.autoSyncTranslationsAutomationId}
                        isWorkflowsStepRun={this.props.isWorkflowsStepRun}
                        stepRunComponentProps={this.props.stepRunComponentProps}
                        selectedPlatform={selectedPlatform}
                    />
                </Content>
                <EmptyStateWrapper
                    releaseStepType={this.props.releaseStepType}
                    appId={this.props.appId}
                    featureSections={[FeatureSection.TranslationCTAs]}
                    releaseId={this.props.releaseId}
                    shouldShowAsTooltip
                    customWrapperStyle={{ position: 'absolute' }}
                >
                    <Footer style={styles.footerStyle}>
                        <TranslationsStepFooter {...this.props} selectedPlatform={selectedPlatform} />
                    </Footer>
                </EmptyStateWrapper>
            </Layout>
        );
    }
}

interface TranslationsStepFooterProps extends ReleaseStepComponentProps {
    workingBranch: string;
    releaseBranch: string;
    isKickedOff: boolean;
    isApproved: boolean;
    userId: string;
    translationsAutomationIsConnected: boolean;
    stepRunId: string;
    stepRunComponentProps?: ReleaseStepOrStepRunComponentProps['stepRunComponentProps'];
    // Set once the step is on Flightpaths, whose actions act on one platform's strings.
    isWorkflowsStepRun?: boolean;
    selectedPlatform?: TranslationsPlatformData;
}

const TranslationsStepFooter: React.FC<TranslationsStepFooterProps> = (props) => {
    const isWorkflowsStep = !!props.isWorkflowsStepRun;
    const legacyUploadIsLoading = useIsLoading(ReleaseStepUpdatable.UploadTranslationStrings, props.releaseId);
    const legacyUploadError = useError(ReleaseStepUpdatable.UploadTranslationStrings, props.releaseId);
    const legacyUploadUpdated = useUpdated(ReleaseStepUpdatable.UploadTranslationStrings, props.releaseId);
    const legacyExportIsLoading = useIsLoading(ReleaseStepUpdatable.ExportTranslationStrings, props.releaseId);
    const legacyExportError = useError(ReleaseStepUpdatable.ExportTranslationStrings, props.releaseId);
    const legacyExportUpdated = useUpdated(ReleaseStepUpdatable.ExportTranslationStrings, props.releaseId);
    const legacyApprovalIsLoading = useIsLoading(ReleaseStepUpdatable.TranslationsApproval, props.releaseId);
    const legacyApprovalUpdated = useUpdated(ReleaseStepUpdatable.TranslationsApproval, props.releaseId);
    const legacyApprovalError = useError(ReleaseStepUpdatable.TranslationsApproval, props.releaseId);

    const [uploadStepRunStrings, stepRunUploadIsLoading, stepRunUploadResponseType, stepRunUploadError] =
        useCallUpdatable(StepRunUpdatable.UploadTranslationsStrings, props.stepRunComponentProps);
    const [exportStepRunStrings, stepRunExportIsLoading, stepRunExportResponseType, stepRunExportError] =
        useCallUpdatable(StepRunUpdatable.ExportTranslationsStrings, props.stepRunComponentProps);
    const [updateStepRunApproval, stepRunApprovalIsLoading, stepRunApprovalResponseType, stepRunApprovalError] =
        useCallUpdatable(StepRunUpdatable.UpdateTranslationsApproval, props.stepRunComponentProps);

    const uploadFilesIsLoading = isWorkflowsStep ? stepRunUploadIsLoading : legacyUploadIsLoading;
    const uploadFilesError = isWorkflowsStep ? stepRunUploadError : legacyUploadError;
    const uploadFilesUpdated =
        isWorkflowsStep ? stepRunUploadResponseType === RunwayResponseType.Success : legacyUploadUpdated;
    const exportFilesIsLoading = isWorkflowsStep ? stepRunExportIsLoading : legacyExportIsLoading;
    const exportFilesError = isWorkflowsStep ? stepRunExportError : legacyExportError;
    const exportFilesUpdated =
        isWorkflowsStep ? stepRunExportResponseType === RunwayResponseType.Success : legacyExportUpdated;
    const approveTranslationsIsLoading = isWorkflowsStep ? stepRunApprovalIsLoading : legacyApprovalIsLoading;
    const translationsApprovalError = isWorkflowsStep ? stepRunApprovalError : legacyApprovalError;
    const translationsApproved =
        isWorkflowsStep ? stepRunApprovalResponseType === RunwayResponseType.Success : legacyApprovalUpdated;

    const dispatch = useDispatch();

    React.useEffect(() => {
        if (uploadFilesError) {
            globalMessage.error(uploadFilesError);
        }
    }, [uploadFilesError]);

    React.useEffect(() => {
        if (uploadFilesUpdated) {
            globalMessage.success('Localization files uploaded!');
        }
    }, [uploadFilesUpdated]);

    React.useEffect(() => {
        if (translationsApprovalError) {
            globalMessage.error(translationsApprovalError);
        }
    }, [translationsApprovalError]);

    React.useEffect(() => {
        if (translationsApproved) {
            globalMessage.success('Translations approval updated!');
        }
    }, [translationsApproved]);

    React.useEffect(() => {
        if (exportFilesError) {
            globalMessage.error(exportFilesError);
        }
    }, [exportFilesError]);

    React.useEffect(() => {
        if (exportFilesUpdated) {
            globalMessage.success('Localization files exported and PR opened!');
        }
    }, [exportFilesUpdated]);

    const onClickApproveTranslations = (isApproved: boolean) => {
        if (isWorkflowsStep) {
            updateStepRunApproval({ isApproved });
            return;
        }
        const request: UpdateTranslationsApprovalRequest = {
            isApproved: isApproved,
            userId: props.userId,
        };
        dispatch(
            tryUpdateReleaseStep(
                ReleaseStepUpdatable.TranslationsApproval,
                {
                    appId: props.appId,
                    orgId: props.orgId,
                    releaseId: props.releaseId,
                    stepRunId: props.stepRunId,
                },
                request,
                props.releaseId,
            ),
        );
    };

    const stepRunActionRequest = (useWorkingBranch: boolean) => ({
        branchName: useWorkingBranch ? props.workingBranch : props.releaseBranch,
        platform: props.selectedPlatform?.platform,
        appStoreType: props.selectedPlatform?.appStoreType,
    });

    const renderUploadExportTranslationsCTAs = () => {
        const hasReleaseBranch = !!props.releaseBranch;
        // A Flightpaths action names a platform, which is unknown until the step run's data loads.
        const actionsDisabled =
            !props.translationsAutomationIsConnected || (isWorkflowsStep && !props.selectedPlatform);
        const isReleaseBranchSameAsWorkingBranch = props.workingBranch === props.releaseBranch;
        const releaseBranchIsDefaultBranch = hasReleaseBranch && props.isKickedOff;
        const defaultUploadContent = () => {
            const branchName = releaseBranchIsDefaultBranch ? props.releaseBranch : props.workingBranch;
            return (
                <Space align="center">
                    <Text>Upload strings from &#39;{branchName}&#39; for translation</Text>
                    <TooltipInfoIcon tooltipContent={uploadTranslationsExplainerMessage()} />
                </Space>
            );
        };
        const defaultExportContent = () => {
            const branchName = releaseBranchIsDefaultBranch ? props.releaseBranch : props.workingBranch;
            return (
                <Space align="center">
                    <Text>Export strings to &#39;{branchName}&#39;</Text>
                    <TooltipInfoIcon tooltipContent={exportTranslationsExplainerMessage()} />
                </Space>
            );
        };

        const onClickUploadTranslations = (useWorkingBranch: boolean) => {
            if (isWorkflowsStep) {
                uploadStepRunStrings(stepRunActionRequest(useWorkingBranch));
                return;
            }
            dispatch(
                tryUpdateReleaseStep(
                    ReleaseStepUpdatable.UploadTranslationStrings,
                    {
                        appId: props.appId,
                        orgId: props.orgId,
                        releaseId: props.releaseId,
                        stepRunId: props.stepRunId,
                    },
                    {
                        branchName: useWorkingBranch ? props.workingBranch : props.releaseBranch,
                    },
                    props.releaseId,
                ),
            );
        };

        const onClickExportTranslations = (useWorkingBranch: boolean) => {
            if (isWorkflowsStep) {
                exportStepRunStrings(stepRunActionRequest(useWorkingBranch));
                return;
            }
            dispatch(
                tryUpdateReleaseStep(
                    ReleaseStepUpdatable.ExportTranslationStrings,
                    {
                        appId: props.appId,
                        orgId: props.orgId,
                        releaseId: props.releaseId,
                        stepRunId: props.stepRunId,
                    },
                    {
                        branchName: useWorkingBranch ? props.workingBranch : props.releaseBranch,
                    },
                    props.releaseId,
                ),
            );
        };

        if (isReleaseBranchSameAsWorkingBranch || !hasReleaseBranch) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <PermissionedActionButton
                        disabled={actionsDisabled}
                        actionType={RunwayUserAction.UAImportExportTranslationStrings}
                        releaseId={props.releaseId}
                        onClick={() => onClickUploadTranslations(!releaseBranchIsDefaultBranch)}
                        loading={uploadFilesIsLoading}
                    >
                        {defaultUploadContent()}
                    </PermissionedActionButton>
                    <PermissionedActionButton
                        disabled={actionsDisabled}
                        actionType={RunwayUserAction.UAImportExportTranslationStrings}
                        releaseId={props.releaseId}
                        onClick={() => onClickExportTranslations(!releaseBranchIsDefaultBranch)}
                        loading={exportFilesIsLoading}
                    >
                        {defaultExportContent()}
                    </PermissionedActionButton>
                </div>
            );
        }

        const altBranchName = releaseBranchIsDefaultBranch ? props.workingBranch : props.releaseBranch;

        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <PermissionedActionButton
                    actionType={RunwayUserAction.UAImportExportTranslationStrings}
                    disabled={actionsDisabled}
                    menu={{
                        items: [
                            {
                                key: 'upload-alternate',
                                label: `Upload localization files from '${altBranchName}' for translation`,
                                icon: <UploadOutlined style={{ color: runwayColors.primary }} />,
                                onClick: () => onClickUploadTranslations(releaseBranchIsDefaultBranch),
                            },
                        ],
                    }}
                    buttonsRender={([leftButton, rightButton]) => [
                        React.cloneElement(leftButton as React.ReactElement<any, string>, {
                            loading: uploadFilesIsLoading,
                        }),

                        React.cloneElement(rightButton as React.ReactElement<any, string>, {
                            loading: uploadFilesIsLoading,
                        }),
                    ]}
                    icon={<UploadOutlined style={{ color: runwayColors.primary }} />}
                    trigger={['click']}
                    loading={uploadFilesIsLoading}
                    onClick={() => onClickUploadTranslations(!releaseBranchIsDefaultBranch)}
                >
                    {defaultUploadContent()}
                </PermissionedActionButton>
                <PermissionedActionButton
                    actionType={RunwayUserAction.UAImportExportTranslationStrings}
                    disabled={actionsDisabled}
                    menu={{
                        items: [
                            {
                                key: 'export-alternate',
                                label: `Export localization files to '${altBranchName}'`,
                                icon: <DownloadOutlined style={{ color: runwayColors.primary }} />,
                                onClick: () => onClickExportTranslations(releaseBranchIsDefaultBranch),
                            },
                        ],
                    }}
                    buttonsRender={([leftButton, rightButton]) => [
                        React.cloneElement(leftButton as React.ReactElement<any, string>, {
                            loading: exportFilesIsLoading,
                        }),

                        React.cloneElement(rightButton as React.ReactElement<any, string>, {
                            loading: exportFilesIsLoading,
                        }),
                    ]}
                    trigger={['click']}
                    loading={exportFilesIsLoading}
                    onClick={() => onClickExportTranslations(!releaseBranchIsDefaultBranch)}
                    icon={<DownloadOutlined style={{ color: runwayColors.primary }} />}
                >
                    {defaultExportContent()}
                </PermissionedActionButton>
            </div>
        );
    };
    return (
        <Space size="large" align="center">
            {renderUploadExportTranslationsCTAs()}
            <PermissionedActionButton
                key="approve"
                releaseId={props.releaseId}
                actionType={RunwayUserAction.UAApproveTranslations}
                onClick={() => onClickApproveTranslations(!props.isApproved)}
                icon={
                    props.isApproved ? <CloseOutlined style={{ color: runwayColors.statusRed }} /> : <CheckOutlined />
                }
                type={props.isApproved ? 'default' : 'primary'}
                loading={approveTranslationsIsLoading}
            >
                {props.isApproved ? 'Rescind approval' : 'Approve translations'}
            </PermissionedActionButton>
        </Space>
    );
};

StepContentTranslations.contextType = ReleaseStepInactiveContext;
const mapProps = (state: RootState, ownProps: ReleaseStepOrStepRunComponentProps) => {
    const { orgId, appId, releaseId, releaseStepType } = ownProps;

    const release = selectRelease(state, releaseId);
    const app = selectApp(state, appId);
    const releaseStep = selectReleaseStep(state, releaseId, releaseStepType);
    // Once translations is on Flightpaths, the step run carries the step's data, refreshed per platform.
    const isWorkflowsStep = !!selectWorkflowEnabledSteps(state, appId)[ReleaseStepType.Translations];
    const stepRun =
        isWorkflowsStep && ownProps.stepRunComponentProps ?
            selectStepRun(state, ownProps.stepRunComponentProps.stepRunId)
        :   undefined;
    const stepRunData = stepRun?.data?.summaryData as Nullable<TranslationsStepRunSummaryData>;
    const step = stepRun ?? releaseStep;
    const stepRunId = step.id;
    const vcsIntegrationId = selectVCSIntegration(state, appId)?.id;
    const stepStatus = step.status;
    const appPlatform = app.appSettings.platform;
    const platform = app.appSettings.platform;
    const appStoreType = app.appSettings.appStoreType;
    const userId = state.user.id;

    const legacyStepData = stepRunData ? undefined : (releaseStep?.data as ReleaseStepTranslationsData);
    const stepData = stepRunData ?? legacyStepData;
    const isApproved = stepData?.isApproved ?? false;

    const version = release.version;
    const isRunwayOrg = selectIsRunwayOrg(state);

    const releaseType = release.type;
    const isKickedOff = stepData?.isKickedOff ?? false;

    const rollbackTargetVersionInfo = release.rollbackTargetVersionInfo;

    const workingBranch = stepData?.workingBranch;
    const releaseBranch = stepData?.releaseBranch;
    const releaseBranchExists = stepData?.doesBranchExist ?? false;

    const translationsDataByPlatform: TranslationsPlatformData[] =
        stepRun ?
            (stepRunData?.platforms ?? []).map(({ platform, appStoreType, translationsData }) => ({
                platform,
                appStoreType,
                translationsData,
            }))
        :   [{ platform, appStoreType, translationsData: legacyStepData?.translationsData }];

    const showBranchSelectionField =
        !!workingBranch && workingBranch !== releaseBranch && release.timelinePhase === ReleaseTimelinePhase.Current;

    const translationsAutomation = selectTranslationsIntegration(state, appId);
    const translationsAutomationIsConnected = !!translationsAutomation;

    const autoSyncTranslationsAutomationId = getAutomationIdByType(
        step.automationRuns,
        AutomationType.AutoSyncTranslations,
    );

    const mergeRunwayPRsAutomationId = getAutomationIdByType(step.automationRuns, AutomationType.AutoMergeRunwayPRs);

    const mergeRunwayPRsEnabled = selectAutomationById(state, appId, mergeRunwayPRsAutomationId)?.enabled;

    return {
        orgId,
        appId,
        releaseId,
        stepRunId,
        autoSyncTranslationsAutomationId,
        translationsDataByPlatform,
        stepRunComponentProps: ownProps.stepRunComponentProps,
        isWorkflowsStepRun: !!stepRun,
        releaseStepType,
        appPlatform,
        stepStatus,
        platform,
        appStoreType,
        version,
        isRunwayOrg,
        releaseType,
        rollbackTargetVersionInfo,
        workingBranch,
        releaseBranch,
        releaseBranchExists,
        showBranchSelectionField,
        isKickedOff,
        mergeRunwayPRsEnabled,
        userId,
        isApproved,
        translationsAutomationIsConnected,
        vcsIntegrationId,
    };
};

const mapDispatch = () => {
    return {};
};
const connector = connect(mapProps, mapDispatch);

type Props = ConnectedProps<typeof connector>;

export default connector(StepContentTranslations);
